import { useEffect, useRef, useState } from 'react'

import styles from './Toast.module.css'

export type ToastType = 'info' | 'success' | 'error' | 'warning'

export interface ToastMessage {
  id: string
  message: string
  type: ToastType
  durationMs?: number
}

// Simple module-level event bus so any component can trigger a toast
// without prop-drilling. Replace with context/Zustand in later phases.
type Listener = (toast: ToastMessage) => void
const listeners: Set<Listener> = new Set()

export function showToast(message: string, type: ToastType = 'info', durationMs = 5000) {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
  listeners.forEach((fn) => fn({ id, message, type, durationMs }))
}

/**
 * Renders the live region and toast stack. Mount once at the app root.
 */
export function Toast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const handler: Listener = (toast) => {
      setToasts((prev) => [...prev, toast])
      const t = setTimeout(() => dismiss(toast.id), toast.durationMs ?? 5000)
      timers.current.set(toast.id, t)
    }
    listeners.add(handler)
    // Capture ref value at registration time for cleanup
    const timerMap = timers.current
    return () => {
      listeners.delete(handler)
      timerMap.forEach(clearTimeout)
    }
  }, [])

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const t = timers.current.get(id)
    if (t) {
      clearTimeout(t)
      timers.current.delete(id)
    }
  }

  return (
    // aria-live="assertive" for errors; "polite" for everything else.
    // We render two regions and route toasts accordingly.
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className={styles.region}
        data-position="bottom-right"
      >
        {toasts
          .filter((t) => t.type !== 'error')
          .map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className={styles.region}
        data-position="bottom-right"
        style={{ pointerEvents: 'none' }}
      >
        {toasts
          .filter((t) => t.type === 'error')
          .map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
      </div>
    </>
  )
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  return (
    <div className={[styles.toast, styles[toast.type]].join(' ')}>
      <p className={styles.message}>{toast.message}</p>
      <button
        type="button"
        aria-label="Dismiss notification"
        className={styles.dismiss}
        onClick={() => onDismiss(toast.id)}
      >
        ✕
      </button>
    </div>
  )
}
